/* eslint-disable max-lines */
import Immutable from 'immutable';
import { matchPath } from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';
import moment from 'moment';
import matches from 'validator/lib/matches';
import isLength from 'validator/lib/isLength';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import UserStore from 'stores/UserStore';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import dates from 'enum/dates';
import {
  PASSWORD_ILLEGAL_CHARS,
  PASSWORD_LOWERCASE_LETTER,
  PASSWORD_MATCH_REGEX,
  PASSWORD_SPECIALCHAR_REGEX,
  PASSWORD_UPPERCASE_LETTER,
} from 'enum/helpers';
import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  SPECIAL_CHARS_PASSWORD,
} from 'enum/inputs';
import { ENUM_PAYMENT_PROCESSOR_IDS } from 'enum/payments';
import {
  GENDER,
  PARSED_PASSWORD_STRENGTH_INCREASED,
} from 'enum/profile';
import { MESSAGE_TYPES } from 'enum/messages';
import {
  SECOND_AS_MILLISECONDS,
  MINUTE_AS_MILLISECONDS,
  MINUTE_AS_SECONDS,
  HOUR_AS_MILLISECONDS,
  HOUR_AS_MINUTES,
  HOUR_AS_SECONDS,
  DAY_AS_HOURS,
  MONTH_AS_DAYS,
  YEAR_AS_DAYS,
  YEAR_AS_MONTHS,
} from 'enum/timeFormat';
import { ASC_ORD } from 'enum/tables';
import time from 'enum/time';
import allUserGroups from 'enum/userGroups';
import routeDefinitions from 'enum/routeDefinitions';
import { getActiveLanguageCode } from 'services/i18nService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import {
  compareGenderPreferenceLanguage,
  getTranslatedGender,
  MULTI_LANGUAGE_GENDERS,
} from 'utils/gender';

import {
  accessLevelFromShortName,
  genderFromShortName,
} from '../firebase/firebaseTranscribers';


const json = require('../../mock-data/footer-links.json');


export {
  impersonatedStudio,
  propFromImpersonatedStudio,
  usernameOrImpersonate,
} from 'utils/impersonate';

export const radioBtnStatesOneClick = {
  keepOneClick: 'keepOneClick',
  saveCard: 'saveCard',
  disableOneClick: 'disableOneClick',
};

export const capitalize = (str) =>
  str
    .toLowerCase()
    .trim()
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.substr(1))
    .join(' ');

// Converts the first letter to uppercase :facepalm:
export const jsUcfirst = (a) => {
  let result = undefined;

  if (/\s/.test(a)) {
    result = a.replace(/\W+(.)/g, (match, chr) => chr.toUpperCase());
  }

  if (a) {
    result = a.charAt(0).toUpperCase() + a.slice(1);
  }

  return result;
};

export const generateID = (prefix, element) => (name) => {
  let generatedId = `${prefix}_${element}`;
  if (name) {
    generatedId += `_${name}`;
  }

  return generatedId;
};

export const roundNumber = (num, scale) => {
  if (!(`${num}`).includes('e')) {
    return +(`${Math.round(`${num}e+${scale}`)}e-${scale}`);
  }
  const arr = (`${num}`).split('e');
  let sig = '';
  if (+arr[1] + scale > 0) {
    sig = '+';
  }

  return +(`${Math.round(`${+arr[0]}e${sig}${+arr[1] + scale}`)}e-${scale}`);

};

// in case of 3 decimal places prevent rounding up
const preserveSecondDecimal = (amount) => amount.toFixed(3).slice(0, -1);

export const convertTokensToDollars = (amount, returnString = false) => {
  if (returnString) {
    // eslint-disable-next-line no-magic-numbers
    return preserveSecondDecimal(amount * 0.1);
  }

  // eslint-disable-next-line no-magic-numbers
  return +preserveSecondDecimal(amount * 0.1);
};

// eslint-disable-next-line no-magic-numbers
export const convertDollarsToTokens = (amount) => +(amount / 0.1).toFixed(2);

export const isValidResponse = (res) => res && res.status === StatusCodes.OK && res.data.status !== 'ERROR';
export const getApiWarnMsg = (fnName, data) => data && `${fnName} ${data.status}:${data.reason}`;

export const naturalOrderSort = (a = '', b = '', order) => {
  const strA = typeof a === 'string' ? a : a.toString();
  const strB = typeof b === 'string' ? b : b.toString();

  return order === ASC_ORD ? strA.localeCompare(strB, undefined, {
    numeric: true,
    sensitivity: 'base',
  }) : strB.localeCompare(strA, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const naturalOrderSortDate = (a = '', b = '', order) => {
  let dateA = typeof a === 'string' ? a : a.toString();
  let dateB = typeof b === 'string' ? b : b.toString();

  dateA = new Date(dateA.split('/').reverse());
  dateB = new Date(dateB.split('/').reverse());

  return order === ASC_ORD ? dateA - dateB : dateB - dateA;
};

/**
 * Sort an array in a specific predefined order.
 * Items that are not in order array will be placed after those that are.
 * If no order array is passed, no sorting will be performed.
 * @param a {string}
 * @param b {string}
 * @param order {string[]}
 * @returns { -1 | 0 | 1 }
 */
export const sortInASpecificOrder = (a, b, order) => {
  if (!order || !Array.isArray(order)) {
    return 0;
  }

  if (!order.includes(a)) {
    return 1;
  }

  if (!order.includes(b)) {
    return -1;
  }

  return order.indexOf(a) - order.indexOf(b);
};

/* eslint-disable max-len */
/**
 * @method getFormattedAmount used to get the formatted amount of the currency and the symbol
 * @param {number} amount
 * @param {number} modifier Multiply with the amount to get the amount to dollars
 * @param {string} currency
 * @param {boolean} forceFloat True if you want to preserve the second decimal
 * @param {boolean} inverse True if you want the amount first and then the currency
 * @param {boolean} country Used for Germany. If true and the currency is '€' the formatted amount will look like this '4.50 €'
 * @returns {string} Formatted amount
 */
/* eslint-enable max-len */

export const getFormattedAmount = (
  amount,
  modifier = 1,
  currency = '$',
  forceFloat = false,
  inverse = false,
  country = false
) => {
  if (isNaN(amount)) {
    amount = 0;
  }

  const amountToDollars = amount * modifier;
  let formattedAmount = undefined;

  if (forceFloat) {
    formattedAmount = preserveSecondDecimal(amountToDollars);
  } else {
    formattedAmount = Number.isInteger(amountToDollars) ? amountToDollars : preserveSecondDecimal(amountToDollars);
  }
  if (country && currency === '€') {
    formattedAmount = formattedAmount.replace('.', ',');

    return `${formattedAmount} ${currency}`;
  }

  return inverse ? `${formattedAmount}${currency}` : `${currency}${formattedAmount}`;
};

export const formatSEKValue = (value) => Math.floor(value.slice(0, value.length - 2));

export const getFormattedDateTime = (timestamp, withTime = true, withSeconds = true) => {
  if (!timestamp || timestamp === -1) {
    return 'N/A';
  }

  const timeFormat = withSeconds ? time.format.full : time.format.short;
  const format = withTime ? `${dates.format.european} ${timeFormat}` : dates.format.european;

  return moment(timestamp).format(format);
};

export const getGenderTranslation = (gender, translate) => {
  switch (gender) {
    case GENDER.MALE:
      return translate('awards.awards.category.male');
    case GENDER.FEMALE:
      return translate('cam4bucks.cam4bucks.121');
    case GENDER.SHEMALE:
      return translate('cam4bucks.cam4bucks.123');
    default:
      return translate('profile.profile.gender.unknown');
  }
};

export const getProcessorNameKey = (processorId) => {
  switch (processorId) {
    case ENUM_PAYMENT_PROCESSOR_IDS.PAXUM:
      return 'payment.v2.paymentSetup.paxum';
    case ENUM_PAYMENT_PROCESSOR_IDS.PAYONEER:
      return 'cam4bucks.cam4bucks.811';
    case ENUM_PAYMENT_PROCESSOR_IDS.ACH:
      return 'payment.v2.paymentSetup.direct.deposit';
    case ENUM_PAYMENT_PROCESSOR_IDS.WIRES:
      return 'payment.v2.paymentSetup.euroWire';
    case ENUM_PAYMENT_PROCESSOR_IDS.EPAY:
      return 'payment.v2.paymentSetup.epayServices';
    case ENUM_PAYMENT_PROCESSOR_IDS.CASH_TO_TOKENS:
      return 'payment.v2.paymentSetup.cashToTokens';
    case ENUM_PAYMENT_PROCESSOR_IDS.SOCIALE:
      return 'payment.v2.paymentSetup.sociale';
    case ENUM_PAYMENT_PROCESSOR_IDS.BITSAFE:
      return 'payment.v2.paymentSetup.bitsafe';
    default:
      return `payment.v2.paymentSetup.processor.${processorId}`;
  }
};

export function generateQueryFromObject(data) {
  let query = '?';
  Object.keys(data).forEach((key) => {
    query = query.concat(`${key}=${data[key]}&`);
  });

  return query.substr(0, query.length - 1);
}

export function getTimeStringWithHours(timer) {
  if (isNaN(timer)) {
    return null;
  }

  // eslint-disable-next-line no-magic-numbers
  return new Date(timer * SECOND_AS_MILLISECONDS).toISOString().substr(11, 8);
}

export function getTimeStringWithoutHours(timer) {
  if (isNaN(timer)) {
    return null;
  }

  // eslint-disable-next-line no-magic-numbers
  return new Date(timer * SECOND_AS_MILLISECONDS).toISOString().substr(14, 5);
}

export function getTimeMinutes(minutes, hours) {
  return String(minutes - (hours * HOUR_AS_MINUTES)).padStart(2, '0');
}

export function secondsToString(secs, hideHours) {
  if (!secs) {
    if (hideHours) {
      return '00:00';
    }

    return '00:00:00';

  }
  secs = parseInt(secs, 10);
  let hours = Math.floor(secs / HOUR_AS_SECONDS);
  let minutes = Math.floor((secs - (hours * HOUR_AS_SECONDS)) / MINUTE_AS_SECONDS);
  let seconds = secs - (hours * HOUR_AS_SECONDS) - (minutes * MINUTE_AS_SECONDS);

  if (hours < 10) {
    hours = `0${hours}`;
  }
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }

  if (hideHours) {
    if (hours === '00') {
      return `${minutes}:${seconds}`;
    }

    return `${hours}:${minutes}:${seconds}`;

  }

  return `${hours}:${minutes}:${seconds}`;

}

export function getDomain() {
  if (window.location && window.location.host) {
    return window.location.host;
  }
  if (typeof window.location === 'undefined') {
    window.location = {};
  }
  window.location.host = document.URL.match(/:\/\/(.[^/]+)/)[1];

  return window.location.host;
}

export function getMasterDomain() {
  let { domain } = document;
  const p = domain.split('.');
  domain = p.slice(1, p.length).join('.');

  return domain;
}

export function getHostName() {
  const { protocol } = window.location;
  const slashes = protocol.concat('//');
  const host = slashes.concat(window.location.hostname);

  return host;
}

export function isCallable(fn) {
  return typeof fn === 'function';
}

export function obfuscatedString(str, numberOfVisibleCharacters = 3) {
  try {
    if (isCallable(str.toString)) {
      str = str.toString();
    }
    const last = str.substr(str.length - numberOfVisibleCharacters, numberOfVisibleCharacters);
    let count = str.length - numberOfVisibleCharacters;
    if (count < 0) {
      count = 0;
    }
    const asteriks = '*'.repeat(count);

    return `${asteriks}${last}`;
  } catch (error) {
    logger.warn(error);

    return str;
  }
}

export function orderApartments(a, b) {
  if (a.orderId < b.orderId) {
    return -1;
  }
  if (a.orderId > b.orderId) {
    return 1;
  }

  return 0;
}

export function counter() {
  let secondsElapsed = 0;
  let currentTimestamp = undefined;
  let startTimestamp = undefined;
  let timerHandler = undefined;

  return {
    start(callback, fixedTime, startTime) {
      const self = this;
      secondsElapsed = 0;
      currentTimestamp = new Date().getTime();
      startTimestamp = startTime || currentTimestamp;
      timerHandler = setInterval(self.processTime, SECOND_AS_MILLISECONDS / 2, callback, fixedTime);
    },
    stop() {
      clearInterval(timerHandler);
    },
    processTime(callback, fixedTime) {
      currentTimestamp = new Date().getTime();
      const secondsElapsedNew = Math.floor((currentTimestamp - startTimestamp) / SECOND_AS_MILLISECONDS);
      if (secondsElapsedNew > secondsElapsed) {
        secondsElapsed = secondsElapsedNew;
        if (fixedTime) {
          callback(fixedTime - secondsElapsed);
        } else {
          callback(secondsElapsed);
        }
      }
    },
  };
}

export function calcPricePerMonth(membership) {
  let price = undefined;
  try {
    if ('referencePrice' in membership.details) {
      let months = membership.details.recurringPeriod / MONTH_AS_DAYS;
      if (membership.details.recurringPeriod === YEAR_AS_DAYS) {
        months = YEAR_AS_MONTHS;
      }
      price = Math.floor((membership.amount / months) * 100) / 100;
    } else {
      price = parseFloat(membership.amount).toFixed(2);
    }
  } catch (error) {
    logger.error(error);
  }

  return price;
}

export const getHumanDate = (timestamp, code = 'en') => moment(timestamp).locale(code).format('LL');
export const timeable = (timestamp) => moment(timestamp).format('HH:mm');
export const validateStrongerPassword = (value) => matches(value, PASSWORD_MATCH_REGEX);
export const validateUpperCasePassword = (value) => matches(value, PASSWORD_UPPERCASE_LETTER);
export const validateLowerCasePassword = (value) => matches(value, PASSWORD_LOWERCASE_LETTER);
export const validateSpecialCharsPassword = (value) => matches(value, PASSWORD_SPECIALCHAR_REGEX);
export const validateIllegalCharsPassword = (value) => matches(value, PASSWORD_ILLEGAL_CHARS);
export const oldValidationPassword = (password) =>
  matches(password, /^[a-z0-9 _]+$/i) &&
  isLength(password, {
    min: 6,
    max: 128,
  });
export const passwordValidation = (password) => {
  if (PARSED_PASSWORD_STRENGTH_INCREASED) {
    const validationForPasswordStrength = validateStrongerPassword(password);
    if (!validationForPasswordStrength) {
      return false;
    }

    const validationForIllegalChars = validateIllegalCharsPassword(password);
    if (!validationForIllegalChars) {
      return false;
    }

    return true;
  }

  return oldValidationPassword(password);
};

export const passwordErrorMsg = (translate, newPassword = '', username) => {
  switch (true) {
    case newPassword.length === 0:
      return translate('profile.change.password.jsp.2');
    case !validateUpperCasePassword(newPassword):
      return translate('profile.profile.password.error.uppercase');
    case !validateLowerCasePassword(newPassword):
      return translate('profile.profile.password.error.lowercase');
    case newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH:
      return translate('profile.profile.password.error.tooShort');
    case !validateSpecialCharsPassword(newPassword):
      return translate('profile.profile.password.error.specialchar', { 0: SPECIAL_CHARS_PASSWORD });
    case username === newPassword:
      return translate('profile.change.password.jsp.1');
    case !validateIllegalCharsPassword(newPassword):
      return translate('profile.profile.password.error.illegal');
    default:
      return translate('profile.profile.password.error.ok');
  }
};

export const passwordErrorMsgSignIn = (newPassword, username) => {
  if (username === null || username === undefined) {
    username = '';
  }
  let isValid = false;
  switch (true) {
    case newPassword.length === 0:
      return { text: 'profile.change.password.jsp.2', isValid };
    case !validateUpperCasePassword(newPassword):
      return { text: 'profile.profile.password.error.uppercase', isValid };
    case !validateLowerCasePassword(newPassword):
      return { text: 'profile.profile.password.error.lowercase', isValid };
    case newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH:
      return { text: 'profile.profile.password.error.tooShort', isValid };
    case !validateSpecialCharsPassword(newPassword):
      return { text: 'profile.profile.password.error.specialchar', isValid };
    case username.toLowerCase() === newPassword.toLowerCase():
      return { text: 'profile.change.password.jsp.1', isValid };
    case !validateIllegalCharsPassword(newPassword):
      return { text: 'profile.profile.password.error.illegal', isValid };
    default:
      isValid = true;

      return { text: 'profile.profile.password.error.ok', isValid };
  }
};

export function mergeTwo(arr1, arr2) {
  const merged = [];
  let index1 = 0;
  let index2 = 0;
  let current = 0;

  while (current < arr1.length + arr2.length) {
    const isArr1Depleted = index1 >= arr1.length;
    const isArr2Depleted = index2 >= arr2.length;

    if (!isArr1Depleted && (isArr2Depleted || arr1[index1].sendDate < arr2[index2].sendDate)) {
      merged[current] = arr1[index1];
      index1 += 1;
    } else {
      merged[current] = arr2[index2];
      index2 += 1;
    }

    current += 1;
  }

  return merged;
}

export function updateHreflangLinks() {
  const hreflang = document.querySelectorAll('link[rel="alternate"]');
  let isGender = false;
  let secParam = undefined;
  let genderKey = undefined;
  if (hreflang) {
    const pathnameSplited = window.location.pathname.split('/');
    if (pathnameSplited.length === 2) {
      secParam = pathnameSplited[1];
      Object.values(MULTI_LANGUAGE_GENDERS).forEach((genObj) => {
        Object.entries(genObj).forEach((entry) => {
          if (entry[1] === secParam.toLowerCase()) {
            isGender = true;
            genderKey = entry[0];
          }
        });
      });
    }
    hreflang.forEach((hl) => {
      const hreflangDomain = hl.href.toLowerCase().split('/', 3).join('/');
      let hrefLangPath = window.location.pathname;
      const langKey = hl.getAttribute('hreflang');
      if (isGender) {
        hrefLangPath = hrefLangPath.replace(secParam, getTranslatedGender(langKey)[genderKey]);
      }
      hl.setAttribute('href', `${hreflangDomain}${hrefLangPath}`);
    });
  }
}

export function trimStartPolyfill(string) {
  let trimmed = '';
  if (String.prototype.trimStart) {
    trimmed = string.trimStart();
  } else if (String.prototype.trimLeft) {
    trimmed = string.trimLeft();
  } else {
    const temp = string.trim();
    const indexOfWord = string.indexOf(temp);
    trimmed = string.slice(indexOfWord, string.length);
  }

  return trimmed;
}

export const handleMetaIndexing = (allFilters) => {
  const notIndexable = document.getElementsByName('ROBOTS')[0] || document.getElementsByName('robots')[0];
  let filterSize = -1;
  const checkFilterSection = () => {
    let showIndexMeta = true;
    const filterTypes = ['contextual', 'locale', 'shared'];
    filterTypes.forEach((ft) => {
      allFilters.get(ft).forEach((context) => {
        if (ft !== 'contextual') {
          const activeFilterSize =
            !context.find(
              (v) =>
                v.get('value') === 'female_group' || v.get('value') === 'solo' || v.get('value') === 'male_female_group'
            ) && context.filter((item) => item.get('active') === true).size;
          filterSize += activeFilterSize;
          if (activeFilterSize > 1) {
            showIndexMeta = false;
          }
        } else {
          context.forEach((conItem) => {
            const activeFilterSize = conItem.filter((item) => item.get('active') === true).size;
            filterSize += activeFilterSize;
            if (activeFilterSize > 1) {
              showIndexMeta = false;
            }
          });
        }
      });
    });
    if (allFilters.get('newPerformer')) {
      filterSize += 1;
    }
    if (allFilters.get('kiiroo')) {
      filterSize += 1;
    }
    if (allFilters.get('hd')) {
      filterSize += 1;
    }
    if (allFilters.get('c2cAllowed')) {
      filterSize += 1;
    }
    if (allFilters.get('source') === 'mobile') {
      filterSize += 1;
    }

    return showIndexMeta;
  };

  const createNotIndexable = () => {
    const meta = document.createElement('meta');
    meta.name = 'ROBOTS';
    meta.content = 'NOINDEX, FOLLOW';
    document.head.appendChild(meta);
  };

  const checkFilterSectionVal = checkFilterSection();

  if (filterSize <= 4 && checkFilterSectionVal) {
    if (notIndexable) {
      notIndexable.parentNode.removeChild(notIndexable);

      return false;
    }

    return false;
  } else if (filterSize > 4 && !notIndexable && !checkFilterSectionVal) {
    createNotIndexable();

    return true;
  }
  if (!notIndexable) {
    createNotIndexable();

    return true;
  }

  return true;

};

export const checkFilterCategories = (allFilters) => {
  let showFiltersTitle = true;
  const filterTypes = ['contextual', 'locale', 'shared'];
  filterTypes.forEach((ft) => {
    if (allFilters.get(ft) !== undefined) {
      allFilters.get(ft).forEach((context) => {
        if (ft !== 'contextual') {
          const activeFilterSize =
            !context.find(
              (v) =>
                v.get('value') === 'female_group' || v.get('value') === 'solo' || v.get('value') === 'male_female_group'
            ) && context.filter((item) => item.get('active') === true).size;
          if (activeFilterSize > 1) {
            showFiltersTitle = false;
          }
        } else {
          context.forEach((conItem) => {
            const activeFilterSize = conItem.filter((item) => item.get('active') === true).size;
            if (activeFilterSize > 1) {
              showFiltersTitle = false;
            }
          });
        }
      });
    }
  });

  return showFiltersTitle;
};

export const checkUserGroup = (user, userGroup) => {
  const isInUserGroup =
    user.user.get('user').get('controlledFeatures') &&
    user.user.get('user').get('controlledFeatures').includes(userGroup);

  return isInUserGroup;
};

export const setInitialGenderFilter = (genderPreference, initialFilters) => {
  switch (genderPreference) {
    case GENDER.MALE:
      return initialFilters
        .updateIn(['shared', 'genders'], (genders) =>
          genders.map((gender) => {
            const genderValue = gender.get('value') === genderPreference;

            return gender.set('active', genderValue);
          })
        )
        .setIn(
          ['shared', 'broadcastType'],
          Immutable.fromJS([
            {
              value: 'male_group',
              urlPath: 'male-group',
              label: 'Male Group',
              active: true,
            },
            {
              value: 'solo',
              urlPath: 'solo',
              label: 'Solo',
              active: true,
            },
            {
              value: 'male_female_group',
              urlPath: 'male-female-group',
              label: 'Male Female Group',
              active: true,
            },
          ])
        );

    case GENDER.FEMALE:
      return initialFilters
        .updateIn(['shared', 'genders'], (genders) =>
          genders.map((gender) => {
            const genderValue = gender.get('value') === genderPreference;

            return gender.set('active', genderValue);
          })
        )
        .setIn(
          ['shared', 'broadcastType'],
          Immutable.fromJS([
            {
              value: 'female_group',
              urlPath: 'female-group',
              label: 'Female Group',
              active: true,
            },
            {
              value: 'solo',
              urlPath: 'solo',
              label: 'Solo',
              active: true,
            },
            {
              value: 'male_female_group',
              urlPath: 'male-female-group',
              label: 'Male Female Group',
              active: true,
            },
          ])
        );
    case GENDER.COUPLE:
      return initialFilters
        .setIn(
          ['shared', 'broadcastType'],
          Immutable.fromJS([
            {
              value: 'male_group',
              urlPath: 'male-group',
              // not visible, so no need for translation
              label: 'Male Group',
              active: true,
            },
            {
              value: 'female_group',
              urlPath: 'female-group',
              // not visible, so no need for translation
              label: 'Female Group',
              active: true,
            },
            {
              value: 'male_female_group',
              urlPath: 'male-female-group',
              // not visible, so no need for translation
              label: 'Male Female Group',
              active: true,
            },
          ])
        )
        .set('couple', true);
    default:
      return initialFilters.set('allGenders', true);
  }
};

/**
 * Returns the cookie
 * @param {string} name - cookie name
 * @return {string}
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  let cookie = undefined;
  if (parts.length === 2) {
    cookie = parts.pop().split(';').shift();
  }

  return cookie;
};

/**
 * Translates url if user has filters in url and switches from one language to another
 * @param {object} location - window location
 * @param {string} genderPreference - genderPreference in english
 * @param {object} history - router history
 */
export const convertMultiLanguageGenderUrls = (location, genderPreference, history, isAnyFilterSelected) => {
  const exceptions = ['tags', 'checkout', 'cams', 'all', 'live-sex', 'page-not-found', 'error-no-profile'];
  const directoryAliasesExceptions = [
    'live-sex',
    'sexe-live',
    'sesso-live',
    'webcam-sexo',
    'sex-cams',
    'cam-sexe',
    'cam-sesso',
    'sex-chat',
    'chat-sexe',
    'chat-porno',
    'chat-sexo',
    'free-sex',
    'cam-gratuit',
    'chat-gratis',
    'gratis-sex',
    'cam-chat',
    'chat-cam',
    'erotik-cam-chat',
    'webcam-chat',
    'sex-video-chat',
    'sexe-video-chat',
    'videochat-sesso',
    'video-chat',
    'sex-video-chat',
    'video-sex-chat',
    'porn-chat',
    'porn-chat',
    'chat-porno',
    'videochat-porno',
    'porno-chat',
    'live-porno-chat',
    'xxx-live',
    'cam-xxx-live',
    'cam-xxx',
    'webcam-xxx',
    'xxx-live',
    'adult-cams',
    'cam-x',
    'cam-adulti',
    'webcam-adultos',
    'erwachsenen-cams',
    'webcam-sex',
    'vuxna-cams',
    'nude-cams',
    'cam-nu',
    'nude-cam',
    'cam-chicas-desnudas',
    'amateur-cams',
    'webcam-amateur',
    'webcam-amatoriali',
    'cam-amateur',
    'amatör-porr',
    'fuck-cams',
    'cam-porno',
    'cam-scopate',
    'webcam-follada',
    'knulla-cam',
    'fick-cams',
    'sex-gratis',
    'webcam-shat',
    'porn-chatt',
  ];

  let urlPath = location.pathname.split('/');

  if (
    urlPath.length > 1 &&
    urlPath[1] !== '' &&
    !exceptions.includes(urlPath[1]) &&
    !isAnyFilterSelected &&
    !directoryAliasesExceptions.includes(urlPath[1])
  ) {
    const translatedGenderPreference = compareGenderPreferenceLanguage(genderPreference, urlPath[1]);
    urlPath[1] = translatedGenderPreference;
    urlPath = urlPath.join('/');
    history.replace(`${urlPath}${location.search}`);
  }
};

/* eslint-disable no-magic-numbers, no-bitwise, new-cap */
export function md5(str) {
  const RotateLeft = function(lValue, iShiftBits) {
    return (lValue << iShiftBits) | (lValue >>> (32 - iShiftBits));
  };

  const AddUnsigned = function(lX, lY) {
    const lX8 = lX & 0x80000000;
    const lY8 = lY & 0x80000000;
    const lX4 = lX & 0x40000000;
    const lY4 = lY & 0x40000000;
    const lResult = (lX & 0x3fffffff) + (lY & 0x3fffffff);
    if (lX4 & lY4) {
      return lResult ^ 0x80000000 ^ lX8 ^ lY8;
    }
    if (lX4 | lY4) {
      if (lResult & 0x40000000) {
        return lResult ^ 0xc0000000 ^ lX8 ^ lY8;
      }

      return lResult ^ 0x40000000 ^ lX8 ^ lY8;
    }

    return lResult ^ lX8 ^ lY8;
  };

  const F = function(x, y, z) {
    return (x & y) | (~x & z);
  };
  const G = function(x, y, z) {
    return (x & z) | (y & ~z);
  };
  const H = function(x, y, z) {
    return x ^ y ^ z;
  };
  const I = function(x, y, z) {
    return y ^ (x | ~z);
  };

  const FF = function(a, b, c, d, x, s, ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(F(b, c, d), x), ac));

    return AddUnsigned(RotateLeft(a, s), b);
  };

  const GG = function(a, b, c, d, x, s, ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(G(b, c, d), x), ac));

    return AddUnsigned(RotateLeft(a, s), b);
  };

  const HH = function(a, b, c, d, x, s, ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(H(b, c, d), x), ac));

    return AddUnsigned(RotateLeft(a, s), b);
  };

  const II = function(a, b, c, d, x, s, ac) {
    a = AddUnsigned(a, AddUnsigned(AddUnsigned(I(b, c, d), x), ac));

    return AddUnsigned(RotateLeft(a, s), b);
  };

  const ConvertToWordArray = function(str) {
    let lWordCount = undefined;
    const lMessageLength = str.length;
    const lNumberOfWordsTemp1 = lMessageLength + 8;
    const lNumberOfWordsTemp2 = (lNumberOfWordsTemp1 - (lNumberOfWordsTemp1 % 64)) / 64;
    const lNumberOfWords = (lNumberOfWordsTemp2 + 1) * 16;
    const lWordArray = Array(lNumberOfWords - 1);
    let lBytePosition = 0;
    let lByteCount = 0;
    while (lByteCount < lMessageLength) {
      lWordCount = (lByteCount - (lByteCount % 4)) / 4;
      lBytePosition = (lByteCount % 4) * 8;
      lWordArray[lWordCount] |= (str.charCodeAt(lByteCount) << lBytePosition);
      lByteCount += 1;
    }
    lWordCount = (lByteCount - (lByteCount % 4)) / 4;
    lBytePosition = (lByteCount % 4) * 8;
    lWordArray[lWordCount] |= (0x80 << lBytePosition);
    lWordArray[lNumberOfWords - 2] = lMessageLength << 3;
    lWordArray[lNumberOfWords - 1] = lMessageLength >>> 29;

    return lWordArray;
  };

  const WordToHex = function(lValue) {
    let WordToHexValue = '';
    let WordToHexValueTemp = '';
    let lByte = undefined;
    let lCount = undefined;
    for (lCount = 0; lCount <= 3; lCount += 1) {
      lByte = (lValue >>> (lCount * 8)) & 255;
      WordToHexValueTemp = `0${lByte.toString(16)}`;
      WordToHexValue += WordToHexValueTemp.substr(WordToHexValueTemp.length - 2, 2);
    }

    return WordToHexValue;
  };

  const Utf8Encode = function(string) {
    string = string.replace(/\r\n/g, '\n');

    let utftext = '';

    for (let n = 0; n < string.length; n += 1) {
      const c = string.charCodeAt(n);

      if (c < 128) {
        utftext += String.fromCharCode(c);
      } else if (c > 127 && c < 2048) {
        utftext += String.fromCharCode((c >> 6) | 192);

        utftext += String.fromCharCode((c & 63) | 128);
      } else {
        utftext += String.fromCharCode((c >> 12) | 224);

        utftext += String.fromCharCode(((c >> 6) & 63) | 128);

        utftext += String.fromCharCode((c & 63) | 128);
      }
    }

    return utftext;
  };

  let x = [];
  let k = undefined;
  let AA = undefined;
  let BB = undefined;
  let CC = undefined;
  let DD = undefined;
  let a = undefined;
  let b = undefined;
  let c = undefined;
  let d = undefined;
  const S11 = 7;
  const S12 = 12;
  const S13 = 17;
  const S14 = 22;
  const S21 = 5;
  const S22 = 9;
  const S23 = 14;
  const S24 = 20;
  const S31 = 4;
  const S32 = 11;
  const S33 = 16;
  const S34 = 23;
  const S41 = 6;
  const S42 = 10;
  const S43 = 15;
  const S44 = 21;

  str = Utf8Encode(str);
  x = ConvertToWordArray(str);
  a = 0x67452301;
  b = 0xefcdab89;
  c = 0x98badcfe;
  d = 0x10325476;

  for (k = 0; k < x.length; k += 16) {
    AA = a;
    BB = b;
    CC = c;
    DD = d;
    a = FF(a, b, c, d, x[k + 0], S11, 0xd76aa478);
    d = FF(d, a, b, c, x[k + 1], S12, 0xe8c7b756);
    c = FF(c, d, a, b, x[k + 2], S13, 0x242070db);
    b = FF(b, c, d, a, x[k + 3], S14, 0xc1bdceee);
    a = FF(a, b, c, d, x[k + 4], S11, 0xf57c0faf);
    d = FF(d, a, b, c, x[k + 5], S12, 0x4787c62a);
    c = FF(c, d, a, b, x[k + 6], S13, 0xa8304613);
    b = FF(b, c, d, a, x[k + 7], S14, 0xfd469501);
    a = FF(a, b, c, d, x[k + 8], S11, 0x698098d8);
    d = FF(d, a, b, c, x[k + 9], S12, 0x8b44f7af);
    c = FF(c, d, a, b, x[k + 10], S13, 0xffff5bb1);
    b = FF(b, c, d, a, x[k + 11], S14, 0x895cd7be);
    a = FF(a, b, c, d, x[k + 12], S11, 0x6b901122);
    d = FF(d, a, b, c, x[k + 13], S12, 0xfd987193);
    c = FF(c, d, a, b, x[k + 14], S13, 0xa679438e);
    b = FF(b, c, d, a, x[k + 15], S14, 0x49b40821);
    a = GG(a, b, c, d, x[k + 1], S21, 0xf61e2562);
    d = GG(d, a, b, c, x[k + 6], S22, 0xc040b340);
    c = GG(c, d, a, b, x[k + 11], S23, 0x265e5a51);
    b = GG(b, c, d, a, x[k + 0], S24, 0xe9b6c7aa);
    a = GG(a, b, c, d, x[k + 5], S21, 0xd62f105d);
    d = GG(d, a, b, c, x[k + 10], S22, 0x2441453);
    c = GG(c, d, a, b, x[k + 15], S23, 0xd8a1e681);
    b = GG(b, c, d, a, x[k + 4], S24, 0xe7d3fbc8);
    a = GG(a, b, c, d, x[k + 9], S21, 0x21e1cde6);
    d = GG(d, a, b, c, x[k + 14], S22, 0xc33707d6);
    c = GG(c, d, a, b, x[k + 3], S23, 0xf4d50d87);
    b = GG(b, c, d, a, x[k + 8], S24, 0x455a14ed);
    a = GG(a, b, c, d, x[k + 13], S21, 0xa9e3e905);
    d = GG(d, a, b, c, x[k + 2], S22, 0xfcefa3f8);
    c = GG(c, d, a, b, x[k + 7], S23, 0x676f02d9);
    b = GG(b, c, d, a, x[k + 12], S24, 0x8d2a4c8a);
    a = HH(a, b, c, d, x[k + 5], S31, 0xfffa3942);
    d = HH(d, a, b, c, x[k + 8], S32, 0x8771f681);
    c = HH(c, d, a, b, x[k + 11], S33, 0x6d9d6122);
    b = HH(b, c, d, a, x[k + 14], S34, 0xfde5380c);
    a = HH(a, b, c, d, x[k + 1], S31, 0xa4beea44);
    d = HH(d, a, b, c, x[k + 4], S32, 0x4bdecfa9);
    c = HH(c, d, a, b, x[k + 7], S33, 0xf6bb4b60);
    b = HH(b, c, d, a, x[k + 10], S34, 0xbebfbc70);
    a = HH(a, b, c, d, x[k + 13], S31, 0x289b7ec6);
    d = HH(d, a, b, c, x[k + 0], S32, 0xeaa127fa);
    c = HH(c, d, a, b, x[k + 3], S33, 0xd4ef3085);
    b = HH(b, c, d, a, x[k + 6], S34, 0x4881d05);
    a = HH(a, b, c, d, x[k + 9], S31, 0xd9d4d039);
    d = HH(d, a, b, c, x[k + 12], S32, 0xe6db99e5);
    c = HH(c, d, a, b, x[k + 15], S33, 0x1fa27cf8);
    b = HH(b, c, d, a, x[k + 2], S34, 0xc4ac5665);
    a = II(a, b, c, d, x[k + 0], S41, 0xf4292244);
    d = II(d, a, b, c, x[k + 7], S42, 0x432aff97);
    c = II(c, d, a, b, x[k + 14], S43, 0xab9423a7);
    b = II(b, c, d, a, x[k + 5], S44, 0xfc93a039);
    a = II(a, b, c, d, x[k + 12], S41, 0x655b59c3);
    d = II(d, a, b, c, x[k + 3], S42, 0x8f0ccc92);
    c = II(c, d, a, b, x[k + 10], S43, 0xffeff47d);
    b = II(b, c, d, a, x[k + 1], S44, 0x85845dd1);
    a = II(a, b, c, d, x[k + 8], S41, 0x6fa87e4f);
    d = II(d, a, b, c, x[k + 15], S42, 0xfe2ce6e0);
    c = II(c, d, a, b, x[k + 6], S43, 0xa3014314);
    b = II(b, c, d, a, x[k + 13], S44, 0x4e0811a1);
    a = II(a, b, c, d, x[k + 4], S41, 0xf7537e82);
    d = II(d, a, b, c, x[k + 11], S42, 0xbd3af235);
    c = II(c, d, a, b, x[k + 2], S43, 0x2ad7d2bb);
    b = II(b, c, d, a, x[k + 9], S44, 0xeb86d391);
    a = AddUnsigned(a, AA);
    b = AddUnsigned(b, BB);
    c = AddUnsigned(c, CC);
    d = AddUnsigned(d, DD);
  }

  const temp = WordToHex(a) + WordToHex(b) + WordToHex(c) + WordToHex(d);

  return temp.toLowerCase();
}
/* eslint-enable no-magic-numbers, no-bitwise, new-cap */

export const formatRegionName = (name) => name
  .toLowerCase()
  .replace('&', 'and')
  .replace(/ /g, '-')
  .replace(/[^a-z-]/g, '');

export const formatCountryName = (name) => {
  if (name === 'Congo, the Democratic Republic of the') {
    return 'democratic-republic-of-the-congo';
  }
  if (name === 'Korea, Democratic People\'s Republic of') {
    return 'north-korea';
  }
  if (name === 'Korea, Republic of') {
    return 'south-korea';
  }

  return name
    .toLowerCase()
    .replace('&', 'and')
    .replace(/ /g, '-')
    .replace(/[^a-z-]/g, '');
};

/**
 * Check if current filters from the URL are present in all possible filters
 * @param {*} appliedFilters Array of current filters from the URL
 * @param {*} allFilters All possible filters object
 * @param {*} user User object
 * @returns true if all the appliedFilters are valid
 */
export const checkIfFilterExists = (appliedFilters, allFilters, user) => {
  const shared = allFilters.get('shared');
  const transNewAttributes = ControlledFeatureService.setUser(user).has(allUserGroups.TRANS_NEW_ATTRIBUTES);
  const locale = allFilters.get('locale');
  const regionals = allFilters.get('regionals');
  const contextualFemale = allFilters.get('contextual').get('female');
  const contextualMale = allFilters.get('contextual').get('male');
  const contextualShemale = allFilters.get('contextual').get('shemale');

  let sharedCat = undefined;
  if (transNewAttributes) {
    sharedCat = ['ethnicities', 'hairColors', 'bodyHairs', 'sexPreferences', 'genders', 'showTypes', 'transBodyTypes'];
  } else {
    sharedCat = ['ethnicities', 'hairColors', 'bodyTypes', 'bodyHairs', 'sexPreferences', 'genders', 'showTypes'];
  }

  const localeCat = ['countries', 'languages'];
  const contxtFemaleCat = ['breastSizes', 'femaleBodyTypes', 'femaleTypes'];
  const contxtMaleCat = ['maleBodyTypes', 'maleTypes', 'maleRoles', 'penisSizes', 'penisTypes'];

  let contxtShemaleCat = undefined;
  if (transNewAttributes) {
    contxtShemaleCat = ['transPenisSizes', 'transPenisTypes', 'transBreastSizes', 'transTypes'];
  } else {
    contxtShemaleCat = ['transPenisSizes', 'transTypes'];
  }

  let availableFilters = Immutable.fromJS(['new', 'livetouch', 'hd', 'mobile',
    'favorites', 'friends', 'c2c', 'boosted', 'recently-watched', 'cam-of-the-day']);

  sharedCat.forEach((group) => {
    availableFilters = availableFilters.concat(shared.get(group).map((el) => el.get('urlPath')));
  });
  contxtFemaleCat.forEach((group) => {
    availableFilters = availableFilters.concat(contextualFemale.get(group).map((el) => el.get('urlPath')));
  });
  contxtMaleCat.forEach((group) => {
    availableFilters = availableFilters.concat(contextualMale.get(group).map((el) => el.get('urlPath')));
  });
  contxtShemaleCat.forEach((group) => {
    availableFilters = availableFilters.concat(contextualShemale.get(group).map((el) => el.get('urlPath')));
  });
  localeCat.forEach((group) => {
    availableFilters = availableFilters.concat(locale.get(group).map((el) => formatCountryName(el.get('name'))));
  });

  availableFilters = availableFilters.concat(regionals.map((region) => formatRegionName(region.get('name'))));

  return appliedFilters.every((v) => availableFilters.includes(v));
};

export const getOptionValue = (option) => {
  if (option.has('value')) {
    if (option.get('value') === 'black' || option.get('value') === 'average') {
      return option.get('urlPath');
    }

    return option.get('value');

  }

  return option.get('code');

};

export const hasIssuers = (val) => val === 'ID';

export const formatName = (name) => {
  if (name.get('name') === 'Congo, the Democratic Republic of the') {
    return 'democratic-republic-of-the-congo';
  }
  if (name.get('name') === 'Korea, Democratic People\'s Republic of') {
    return 'north-korea';
  }
  if (name.get('name') === 'Korea, Republic of') {
    return 'south-korea';
  }

  return name
    .get('name')
    .toLowerCase()
    .replace('&', 'and')
    .replace(/ /g, '-')
    .replace(/[^a-z-]/g, '');
};

export const getAgeVerificationUrl = (user) =>
  `/${encodeURIComponent(user.username)}/settings/performerhub/age-verification`;

export const removeResolutionFromImageSrc = (imgSrc) => {
  const resolution = '400x300';
  const twoSlashes = '--';
  const twoDots = '..';
  let newImgSrc = imgSrc;

  if (newImgSrc.includes(resolution)) {
    newImgSrc = newImgSrc.replace(resolution, '');
  }
  if (newImgSrc.includes(twoSlashes)) {
    newImgSrc = newImgSrc.replace(twoSlashes, '-');
  }
  if (newImgSrc.includes(twoDots)) {
    newImgSrc = newImgSrc.replace(twoDots, '.');
  }

  return newImgSrc;
};

const parseLightRosterUserLegend = {
  gender: 0,
  accessLevel: 1,
  isRoomModerator: 2,
  isSilenced: 3,
  stealthLevel: 4,
  isFanclubSubscribed: 5,
  shouldNotifyBroadcaster: 6,
  viewerType: 7,
  isFriend: 8,
  isFavorite: 9,
  hasTokens: 10,
  recentSpendingTier: 11,
};

export const parseLightRosterUser = (key, userStr) => {
  const gender = userStr.charAt(parseLightRosterUserLegend.gender);
  const accessLevel = userStr.charAt(parseLightRosterUserLegend.accessLevel);
  const isRoomModerator = userStr.charAt(parseLightRosterUserLegend.isRoomModerator);
  const isSilenced = userStr.charAt(parseLightRosterUserLegend.isSilenced);
  const stealthLevel = userStr.charAt(parseLightRosterUserLegend.stealthLevel);
  const isFanclubSubscribed = userStr.charAt(parseLightRosterUserLegend.isFanclubSubscribed);
  const shouldNotifyBroadcaster = userStr.charAt(parseLightRosterUserLegend.shouldNotifyBroadcaster);
  const viewerType = userStr.charAt(parseLightRosterUserLegend.viewerType);
  const isFriend = userStr.charAt(parseLightRosterUserLegend.isFriend);
  const isFavorite = userStr.charAt(parseLightRosterUserLegend.isFavorite);
  const hasTokens = userStr.charAt(parseLightRosterUserLegend.hasTokens);
  const recentSpendingTier = userStr.charAt(parseLightRosterUserLegend.recentSpendingTier);

  const firstDelimiter = userStr.indexOf('$');
  const secondDelimiter = userStr.indexOf('$', firstDelimiter + 1);
  const userId = userStr.substring(firstDelimiter + 1, secondDelimiter);

  const thirdDelimiter = userStr.indexOf('$', secondDelimiter + 1);
  const isBroadcasting = userStr.substring(thirdDelimiter + 1, thirdDelimiter + 2);

  return {
    originalUsername: key,
    isOnline: true,
    gender: genderFromShortName(gender),
    accessLevel: accessLevelFromShortName(accessLevel),
    isRoomModerator,
    isSilenced: isSilenced === '1',
    stealthLevel: parseInt(stealthLevel, 10),
    isFanclubSubscribed,
    notifyBroacaster: shouldNotifyBroadcaster,
    viewerType: parseInt(viewerType, 10),
    isFriendWithBroadcaster: parseInt(isFriend, 10),
    isFavoriteOfBroadcaster: parseInt(isFavorite, 10),
    hasTokens: parseInt(hasTokens, 10),
    recentSpendingTier: parseInt(recentSpendingTier, 10),
    userID: parseInt(userId, 10),
    isBroadcasting,
  };
};

/**
 * Returns an array with arrays of the given size.
 *
 * @param myArray {Array} Array to split
 * @param chunkSize {Integer} Size of every group
 */
export const chunkArray = (myArray = [], chunkSize = 1) => {
  const results = [];
  const arrCopy = [...myArray];

  while (arrCopy.length) {
    results.push(arrCopy.splice(0, chunkSize));
  }

  return results;
};

export const isNotEmpty = (value) => (value instanceof Object && Object.keys(value).length) || value.length;

export const getExtension = (filename) => {
  const result = filename.match(/[^.]*$/);

  return result?.[0] || '';
};

export const shorterFileName = (file) => {
  const fileName = file.name;
  const FILE_NAME_MAX_LENGTH = 20;
  if (fileName.length > FILE_NAME_MAX_LENGTH) {
    const extension = getExtension(fileName);

    return `${fileName.substring(0, FILE_NAME_MAX_LENGTH)}...${extension}`;
  }

  return fileName;
};

export const resolveGenderPreference = (user) => {
  const userStoredGenderPreference = user.user.getIn(['user', 'genderPreference']);

  if (userStoredGenderPreference && userStoredGenderPreference !== GENDER.UNKNOWN) {
    return userStoredGenderPreference;
  }

  return GENDER.FEMALE;

};

export const msToTime = (duration) => {
  let seconds = Math.floor((duration / SECOND_AS_MILLISECONDS) % MINUTE_AS_SECONDS);
  let minutes = Math.floor((duration / MINUTE_AS_MILLISECONDS) % HOUR_AS_MINUTES);
  let hours = Math.floor((duration / HOUR_AS_MILLISECONDS) % DAY_AS_HOURS);

  hours = hours < 10 ? `0${hours}` : hours;
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  seconds = seconds < 10 ? `0${seconds}` : seconds;

  return `${hours}:${minutes}:${seconds}`;
};

export const isUserSpyTicketHolder = (obj, username) => Object.hasOwn(obj, username);

export const shouldShowTokenAwarenessOverlay = (user, isBroadcasterChat) => {
  const { isPerformer, accessLevel } = user;
  const hasControlledFeature = ControlledFeatureService.setUser(user).has(allUserGroups.TOKEN_AWARENESS_OVERLAY);
  const isUserLoggedIn = Boolean(user.username);
  const hidePromoOverlay = CookieStorageService.get('cam4_hide_promo_overlay');
  const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const isBasicUser = accessLevel < ACCESS_LEVEL_NUM.GOLD;
  const created = user.user.getIn(['user', 'created']);
  const userCreated = created && moment(created);
  const diff = created && moment().diff(userCreated, 'days');
  const olderThanWeekOrGuest = created ? diff > 7 : true;
  const hasPurchases =
    isUserLoggedIn && !isGuestBilling && user.user.get('user').get('paymentInfo').get('hasPurchases');
  const shouldShowOverlay =
    hasControlledFeature &&
    !hasPurchases &&
    isBasicUser &&
    !isPerformer &&
    olderThanWeekOrGuest &&
    hidePromoOverlay !== 'invisible' &&
    !isBroadcasterChat;

  return shouldShowOverlay;
};

export const isMobileRender = () => {
  const MIN_DESKTOP_WIDTH = 897;
  const isMobileBreakpoint = screen.width < MIN_DESKTOP_WIDTH;
  const bellowMinDesktopWidth = window.innerWidth < MIN_DESKTOP_WIDTH;

  return (isMobileOnly && isMobileBreakpoint) || bellowMinDesktopWidth;
};

export const chatFontSettings = (isDesktop, chatSettings) => {
  if (isDesktop && chatSettings) {
    return { fontSize: chatSettings.normalScreenFontSize };
  }

  return {};
};

export const noFilterForCAM4Staff = (senderAccessLevel, performerName, senderUsername) =>
  senderAccessLevel === MESSAGE_TYPES.ADMIN ||
  senderAccessLevel === MESSAGE_TYPES.COACH ||
  senderAccessLevel === MESSAGE_TYPES.VERIFIED_ROOM_MODERATOR ||
  senderAccessLevel === MESSAGE_TYPES.USER_MODERATOR ||
  senderAccessLevel === MESSAGE_TYPES.MODERATOR ||
  performerName === senderUsername;
// toFixed retuns type string
// in case we need to get 0.3 in number type we can use this helper function
// toFixed(0.299999998, 1, 10) => 0.3
export const toFixedNumber = (num, digits, base) => {
  const pow = Math.pow(base || 10, digits || 1);

  return Math.round(num * pow) / pow;
};

export const isMyProfileHelperFunc = (userObject, performerName) =>
  Boolean(performerName) &&
  Boolean(userObject?.username) &&
  userObject.username.toLowerCase() === performerName.toLowerCase();

export const secondsToTime = (e) => {
  const m = Math.floor((e % HOUR_AS_SECONDS) / MINUTE_AS_SECONDS)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(e % MINUTE_AS_SECONDS)
    .toString()
    .padStart(2, '0');

  return `${m}:${s}`;
};

export const getBrowserUID = () => {
  const browserDetect = {
    uniqueProps: [],
    browserUID: '',
  };

  const stringType = typeof 'testSting';
  const numberType = typeof 1;

  function putData(string) {
    string += '';
    if (browserDetect.uniqueProps.indexOf(string) < 0) {
      browserDetect.uniqueProps.push(string);
      browserDetect.browserUID += `${string}`;
    }
  }
  function stringify(object) {
    for (const i in object) {
      if (typeof object[i] === stringType || typeof object[i] === numberType) {
        putData(i);
        putData(object[i]);
      } else {
        const keyToName = `${i}`;
        putData(keyToName);
      }
    }
  }

  browserDetect.browserUID = '';
  browserDetect.uniqueProps = [];

  if (window.navigator) {
    if (window.navigator.plugins) {
      for (const i in window.navigator.plugins) {
        const plugin = window.navigator.plugins[i];
        for (const j in plugin) {
          if (typeof plugin[j] === stringType) {
            putData(j);
            putData(plugin[j]);
          } else {
            stringify(plugin[j]);
          }
        }
      }
    }
  }

  browserDetect.browserUID = browserDetect.browserUID.replace(/\s/g, '');

  return md5(browserDetect.browserUID);
};

export const getSessionUuid = (user) => md5(getBrowserUID() + JSON.stringify(new Date()) + JSON.stringify(user));

export const loadScript = (scriptId, src, callback, errorHandler) => {
  const existingScript = document.getElementById(scriptId);
  if (existingScript) {
    return existingScript;
  }

  const script = document.createElement('script');
  script.src = src;
  script.id = scriptId;
  script.onload = () => {
    if (callback && typeof callback === 'function') {
      callback();
    }
  };
  script.onerror = () => {
    errorHandler?.();
  };

  return document.body.appendChild(script);
};

export const unloadScript = (element) => {
  if (!element?.tagName.toLowerCase() === 'script') {
    return;
  }
  document.body.removeChild(element);
};
export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }

  return 'unknown';
};

export const sortOutSameDates = (month) => {
  if (month.length) {
    const monthLength = moment(month[0].startDate).daysInMonth();
    const completeMonth = [];

    for (let i = 0; i < monthLength; i += 1) {
      completeMonth.push([]);
    }

    month.forEach((md) => {
      const monthDate = moment(md.startDate).get('date') - 1;
      completeMonth[monthDate].push(md);
    });

    return completeMonth;
  }

  return [];
};

export const shouldShowEditButton = (userObject, performerName) =>
  isMyProfileHelperFunc(userObject, performerName) && UserStore.hasCF(allUserGroups.BROADCASTER_PROFILE_EDIT_LINKS);

/**
 * debug method to extract delta between 2 objects
 * @returns {{}} hash with changed properties as key and [old, new] values as value ex {a: [1, 2]}
 */
export const objectDelta = (v1, v2) => {
  try {
    const keys = new Set([...Object.keys(v1), ...Object.keys(v2)]);
    const delta = {};
    keys.forEach((k) => {
      if (typeof v1[k] === 'function' || typeof v2[k] === 'function') {
        // nothing
      } else if (v1[k] === null && v2[k] === null) {
        // nothing
      } else if (typeof v1[k] === 'object' || typeof v2[k] === 'object') {
        const propDelta = objectDelta(v1[k], v2[k]);
        if (Object.keys(propDelta).length > 0) {
          delta[k] = [v1[k], v2[k]];
        }
      } else if (v1[k] !== v2[k]) {
        delta[k] = [v1[k], v2[k]];
      }
    });

    return delta;
  } catch (e) {
    return { error: 'unable to extract delta' };
  }
};

/**
 * creates a range of the form of an interval [from, to) where to is exclusive
 * @param from the start of the interval
 * @param to the end of the interval, this is exclusive
 * @returns [] array with the consecutive numbers in the range
 */
export const range = (from, to) => [...Array(to).keys()].slice(from, to);

export const getCappedPercentageOfTotal = (total, part = 0) => {
  const percent = (part / total) * 100;
  if (percent > 100) {
    return 100;
  }

  return percent;
};

export const checkRouteMatch = (path, routes) => routes.some((route) => matchPath(path, { path: route, exact: true }));

export const getRouteMatch = (path, routes) => matchPath(path, { path: routes, exact: true });

export const arrayUniqueByKey = (array, key) => ([...new Map(array.map((item) => [item[key], item])).values()]);

export const isStringHTMLTag = (string) => /<\/?[a-z][\s\S]*>/i.test(string);

export const isAvailableToMove = (location) => location.pathname !== routeDefinitions.weakPasswordReset;

export const focusChatInputField = () => {
  const chatInput = document.getElementsByClassName('inputField')[0];
  setTimeout(() => chatInput?.focus(), 0);
};

export const handleIzootoGenderUpdate = (gender) => {
  if (window._izq) {
    window._izq.push(['userProfile', { 'add': { 'Gender': gender } }]);
  }
};

export const noOp = () => undefined;

export const compareDates = (a, b) => moment(a).diff(b);

export const addPercentage = (num, percentage) => Math.ceil(num * (1 + (percentage / 100)));

export const getCam4ModelsUrl = () => {
  const items = json.footer[0].links[0].urls;
  const activeLanguageCode = getActiveLanguageCode();

  return items[activeLanguageCode] || items.en;
};

export const shouldApplyFingerprintHeaders = () => {
  if (CookieStorageService.get('fpjs-headers-sent')) {
    return false;
  }

  const interval = ConfigurationService.get(ConfigurationKeys.FINGERPRINT_REFRESH_INTERVAL_SECONDS);
  const expires = moment().add(interval, 'seconds').toDate();
  CookieStorageService.set('fpjs-headers-sent', true, { path: '/', expires });

  return true;
};
